import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowUpRight, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHero, SectionHeader, CTASection } from '@/components/shared/Section';
import { SERVICES, INDUSTRIES, VALUES } from '@/data/content';
import { HOME } from '@/constants/testIds';

const heroImg = 'https://images.pexels.com/photos/27206533/pexels-photo-27206533.jpeg';
const workforceImg = 'https://images.unsplash.com/photo-1573164574511-73c773193279?w=1200&q=80';

const stats = [
  { v: '7+', l: 'Industries Served' },
  { v: '6', l: 'Service Pillars' },
  { v: 'GCC', l: 'Regional Focus' },
  { v: '100%', l: 'Customer-Centric' },
];

const ServiceTile = ({ s, idx }) => {
  const Icon = Icons[s.icon] || Sparkles;
  const isLg = s.size === 'lg';
  const accentBg =
    s.accent === 'gold'
      ? 'bg-[#003B73] text-white'
      : s.accent === 'blue'
      ? 'bg-white border border-slate-200'
      : 'bg-slate-50 border border-slate-200';
  const iconBg =
    s.accent === 'gold'
      ? 'bg-tito-gold/20 text-tito-gold'
      : s.accent === 'blue'
      ? 'bg-blue-50 text-tito-blue'
      : 'bg-white text-tito-blue border border-slate-200';
  const titleClr = s.accent === 'gold' ? 'text-white' : 'text-slate-900';
  const bodyClr = s.accent === 'gold' ? 'text-blue-100/80' : 'text-slate-600';

  return (
    <Link
      to={`/services#${s.slug}`}
      data-testid={HOME.serviceCard}
      className={`group relative ${accentBg} rounded-sm p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl flex flex-col ${
        isLg ? 'lg:col-span-2 lg:row-span-2 md:p-10' : ''
      }`}
      style={{ animationDelay: `${idx * 80}ms` }}
    >
      <div className={`inline-flex h-11 w-11 items-center justify-center rounded-sm ${iconBg}`}>
        <Icon className="h-5 w-5" />
      </div>
      <h3 className={`mt-6 font-display font-semibold ${isLg ? 'text-2xl md:text-3xl' : 'text-xl'} ${titleClr}`}>
        {s.title}
      </h3>
      <p className={`mt-3 text-sm md:text-base leading-relaxed ${bodyClr}`}>{s.short}</p>
      {isLg && (
        <ul className="mt-5 space-y-2">
          {s.items.slice(0, 4).map((it) => (
            <li key={it} className="flex items-center gap-2 text-sm text-blue-100/90">
              <span className="h-1 w-1 rounded-full bg-tito-gold" />
              {it}
            </li>
          ))}
        </ul>
      )}
      <div className={`mt-auto pt-6 flex items-center gap-1.5 text-sm font-medium ${s.accent === 'gold' ? 'text-tito-gold' : 'text-tito-blue'}`}>
        Explore <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </div>
    </Link>
  );
};

export default function HomePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#003B73] text-white">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${heroImg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#003B73] via-[#003B73]/95 to-[#001E3D]" />
        <div className="absolute -right-32 top-20 h-96 w-96 rounded-full bg-tito-gold/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 reveal">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-tito-gold animate-pulse" />
                <span className="text-xs font-medium tracking-wider uppercase text-blue-100/90">A division of TITO Corporate Services Provider</span>
              </div>
              <h1 className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-[5.25rem] tracking-tight leading-[1] text-white">
                Empowering businesses through smart{' '}
                <span className="text-tito-gold italic font-medium">workforce</span> & growth solutions.
              </h1>
              <p className="mt-7 text-base sm:text-lg text-blue-100/85 max-w-xl leading-relaxed">
                A Dubai-based business advisory and workforce solutions partner — bridging the gap between ambitious businesses and exceptional opportunities.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link to="/contact">
                  <Button
                    data-testid={HOME.heroCta}
                    className="rounded-sm bg-tito-gold hover:bg-yellow-500 text-[#001E3D] font-semibold h-12 px-7 gap-2"
                  >
                    Start a Conversation
                    <ArrowUpRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button
                    data-testid={HOME.heroSecondary}
                    variant="ghost"
                    className="rounded-sm h-12 px-7 text-white hover:bg-white/10 border border-white/20 gap-2"
                  >
                    Explore Services
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 reveal" style={{ animationDelay: '180ms' }}>
              <div className="relative">
                <div className="absolute -inset-2 bg-tito-gold/30 rounded-sm blur-2xl" />
                <div className="relative aspect-[4/5] rounded-sm overflow-hidden border border-white/10">
                  <img src={workforceImg} alt="Professional workforce" className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#001E3D]/60 via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 rounded-sm bg-white/90 backdrop-blur-xl p-5">
                    <div className="eyebrow text-tito-blue/70">Our Mission</div>
                    <p className="mt-2 text-slate-800 font-medium leading-snug">
                      Bridge the gap between businesses and opportunities through innovation, technology and professional support.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-y-6 border-t border-white/10 pt-10">
            {stats.map((s) => (
              <div key={s.l}>
                <div className="font-display font-bold text-3xl md:text-4xl text-white">
                  {s.v}
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-blue-100/60">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-slate-200 bg-white">
        <div className="marquee py-5">
          <div className="marquee-track text-slate-400 font-display text-sm uppercase tracking-[0.3em]">
            {[...Array(2)].flatMap((_, k) =>
              ['Business Growth', 'Workforce Solutions', 'Employer Branding', 'HR Technology', 'Corporate Services', 'Career Development'].map((t) => (
                <span key={`${k}-${t}`} className="flex items-center gap-16">
                  {t} <span className="text-tito-gold">✦</span>
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Services bento */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHeader
            eyebrow="What we do"
            title="Six pillars. One growth partner."
            description="From recruitment to corporate setup, our service pillars are designed to compound — small wins today, sustainable growth tomorrow."
          />
          <Link to="/services">
            <Button variant="ghost" className="rounded-sm text-tito-blue hover:bg-blue-50 gap-1.5">
              All Services <ArrowUpRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-4 auto-rows-fr">
          {SERVICES.map((s, i) => (
            <ServiceTile key={s.slug} s={s} idx={i} />
          ))}
        </div>
      </section>

      {/* Industries */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeader
            eyebrow="Industries we serve"
            title="Operating where Dubai builds, hires and grows."
            description="Sector-aware workforce and business solutions tailored to the operating realities of seven core industries."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {INDUSTRIES.map((ind, i) => {
              const Icon = Icons[ind.icon] || Sparkles;
              return (
                <div
                  key={ind.name}
                  data-testid={HOME.industryCard}
                  className="group relative bg-[#F8FAFC] border border-slate-200 rounded-sm p-6 transition-all duration-300 hover:-translate-y-1 hover:border-tito-gold hover:shadow-lg"
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-white text-tito-blue border border-slate-200 group-hover:bg-tito-blue group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display font-semibold text-lg text-slate-900">{ind.name}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{ind.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="Our core values"
              title="The principles behind every engagement."
              description="Six values that shape how we hire, advise, and partner with the businesses that trust us."
            />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
            {VALUES.map((v, i) => (
              <div
                key={v.name}
                data-testid={HOME.valueCard}
                className="bg-white p-7 transition-colors hover:bg-blue-50/40"
              >
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-tito-gold text-sm">0{i + 1}</span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
                <h4 className="mt-4 font-display font-semibold text-lg text-slate-900">{v.name}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us preview */}
      <section className="bg-[#001E3D] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="eyebrow text-tito-gold">Why TITO</div>
              <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                Practical advice, delivered with discipline.
              </h2>
              <p className="mt-5 text-blue-100/85 leading-relaxed">
                Backgrounds spanning banking, business development, real estate and relationship management — applied to your business with technology-driven efficiency.
              </p>
              <Link to="/why-choose-us">
                <Button className="mt-8 rounded-sm bg-white text-tito-blue hover:bg-blue-50 h-11 px-6 gap-2 font-semibold">
                  See why businesses choose us <ArrowUpRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                'Experienced business professionals',
                'Personalized business solutions',
                'Technology-driven approach',
                'Customer-centric service',
                'Strategic partnerships',
                'Professional integrity',
              ].map((t) => (
                <div key={t} className="flex items-start gap-3 p-5 rounded-sm bg-white/5 border border-white/10 hover:border-tito-gold/40 transition-colors">
                  <CheckCircle2 className="h-5 w-5 text-tito-gold shrink-0 mt-0.5" />
                  <span className="text-sm text-blue-50">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
